import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '../../../lib/api';
import { ApiProblemError, WriteRefusedError, fetchJson } from '../../../lib/apiFetchJson';
import { InstantActionDefinitionsResponseSchema, type InstantActionDefinition } from '@shared/schemas/instant-action-definition';
import { ExecuteActionResponseSchema, type ExecuteActionResponse } from '@shared/schemas/execute-action-response';
import type { RequestEntry } from '@shared/flow/requests';
import type { PlayOption } from '@shared/flow/play-catalog';
import { canonicalizeEmail } from '@shared/normalize-email';
import { useEnrollQueue } from '../EnrollQueue/EnrollQueueContext';
import { prependOptimisticRequest } from '../EnrollQueue/requests-hooks';
import { useContactStateDetail } from './hooks';

/**
 * Headless contact-action/enroll logic (plan 005 Wave 0 U1a) — lifted
 * verbatim out of `ContactActionMenus.tsx` (flow-workflow-requests U7) so V1's
 * "Actions ▾" / "Queue for Play ▾" dropdowns and the V2 band's dropdown
 * triggers (U3/U9) run the exact same fire/enroll code. This module is
 * React-free of JSX and Glaze-free by design — it owns data + mutations
 * only; every consumer supplies its own chrome (portal menu here, Glaze
 * `Dropdown` in V2) and owns its own menu open/close state (see `fire`'s doc
 * comment below for the one intentional behavioural subtraction).
 */

/**
 * Menu membership is DATA-DRIVEN (flow-workflow-requests ITEM 1) via
 * `ActionDefinition.menuSurface`, set by the instant-action spec registry's
 * boot-time reconciler (src/services/flow/instant-actions.ts). `GET
 * /api/v4/action-definitions?menuSurface=contact_actions` returns exactly
 * "enabled + exposed on THIS menu" — no play-bound connectors, and no
 * frontend allowlist to keep in sync (retires the old INSTANT_ACTION_SLUGS
 * hardcode this component used to filter with). Adding a future instant
 * action (e.g. `route_to_gtme`) requires only a new spec entry in the
 * registry — this component needs no change.
 */
const CONTACT_ACTIONS_MENU_SURFACE = 'contact_actions';

export const ACTION_DEFS_QUERY_KEY = ['flow-contact-card-instant-actions'] as const;

/**
 * Fetches the (now auth-gated, U3; now menuSurface-filtered, ITEM 1)
 * action-definitions catalog. A dedicated query key (not `ActionsMenu.tsx`'s
 * `v4-action-definitions`) — that menu lives in a different feature
 * (view-system's account-scoped Global Actions) with its own context-key
 * readiness logic; sharing its cache entry here would couple two unrelated
 * surfaces to the same key for no real benefit (both hit the same cheap,
 * indefinitely-cached endpoint independently).
 */
export function useInstantActionDefinitions() {
  const { apiFetch } = useApi();
  return useQuery<InstantActionDefinition[]>({
    queryKey: ACTION_DEFS_QUERY_KEY,
    staleTime: Infinity,
    queryFn: () =>
      fetchJson(
        apiFetch,
        InstantActionDefinitionsResponseSchema,
        `/api/v4/action-definitions?menuSurface=${CONTACT_ACTIONS_MENU_SURFACE}`,
      ),
  });
}

/**
 * The three EXECUTE-WIRE contact fields this component authoritatively holds
 * and passes verbatim to `POST /api/v4/actions/:slug/execute`
 * (`contact_id`/`contact_email`/`account_id`) — the only `source_field`
 * values a definition's `required_contact_fields` can be gated on. Mirrors
 * the backend's `GATEABLE_CONTACT_SOURCE_FIELDS`
 * (`@shared/schemas/instant-action-definition`); `'name'` is deliberately
 * absent from both — the execute wire never carries one (hydrated
 * server-side instead, see that map's doc comment).
 *
 * A `source_field` this map doesn't recognize (a malformed/unexpected server
 * response) degrades to "not filtered on" below — mirrors `PlayPicker.tsx`'s
 * posture (see its `emailFiltered` comment) that a bad server-side
 * derivation must never hide an action outright; the server's own 422 at
 * fire time is the real backstop.
 */
export function missingRequiredFields(
  def: InstantActionDefinition,
  contactFieldValues: Record<string, string | null>,
): InstantActionDefinition['required_contact_fields'] {
  // `?? []` stays as a belt-and-braces guard for hand-built definitions (the wire
  // schema already defaults a missing key to `[]`) — degrade to "nothing
  // required", never to "hide everything".
  return (def.required_contact_fields ?? []).filter((field) => {
    // `hasOwnProperty`, not `in` — `in` also matches inherited
    // `Object.prototype` keys (`toString`, `constructor`, …). A malformed
    // `source_field` value equal to one of those would pass an `in` check,
    // then hand a FUNCTION (not a contact value) to `canonicalizeEmail`,
    // throwing at render time instead of degrading to "not filtered on"
    // (Codex review, 2026-08-13). `Object.hasOwn` would read cleaner but
    // isn't in this project's ES2020 lib target.
    if (!Object.prototype.hasOwnProperty.call(contactFieldValues, field.source_field)) return false;
    return canonicalizeEmail(contactFieldValues[field.source_field]) === null;
  });
}

export const READ_ONLY_TITLE = (what: string) => `${what} are read-only while impersonating`;

export interface UseFireInstantActionArgs {
  contactId: string;
  /** Display name for toasts/optimistic feed rows; null renders a generic fallback. */
  contactName: string | null;
  contactEmail: string | null;
  accountId: string | null;
  /**
   * Called after an instant action fires successfully (the sidebar has already
   * jumped to the run's Actions detail). Hosts whose own chrome would COVER
   * that detail — the search card popover — pass their dismiss here; the
   * contact-card header passes nothing.
   */
  onFired?: () => void;
}

/**
 * "Actions ▾" logic — fires an instant global action (`POST
 * /api/v4/actions/:slug/execute`) with this contact's identity attached
 * (U3's `contact_id`/`contact_email`/`account_id` body fields). On success:
 * toast, an optimistic prepend into the Requests feed cache (U6's
 * `prependOptimisticRequest` helper — replaced by the real row once the
 * server's `execution_status_changed` SSE invalidate triggers a refetch),
 * and a jump straight into that execution's Requests-tab detail
 * (`openRequestDetail`, U6). On failure: an error toast and nothing is
 * prepended — there is no stale optimistic entry to clean up because the
 * prepend only happens after a confirmed success.
 *
 * R5 (QA round 2, recurring complaint): the confirmed-success feedback
 * (`openRequestDetail` + toast) must NEVER depend on the best-effort
 * optimistic-cache prepend succeeding. Order is deliberate:
 * `openRequestDetail` first, then the toast, then the prepend LAST inside
 * its own try/catch — a cache-write error (e.g. a dedup collapse) can only
 * ever cost the optimistic row, never the rep's confirmation that the fire
 * actually worked.
 *
 * `fire()` does NOT close any caller-owned menu (the one intentional
 * behavioural subtraction from the pre-lift `handleFire`) — the caller owns
 * its own menu's open state and closes it itself before/alongside calling
 * `fire`.
 */
export function useFireInstantAction({ contactId, contactName, contactEmail, accountId, onFired }: UseFireInstantActionArgs) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { readOnly, openRequestDetail } = useEnrollQueue();
  const { data: definitions, isLoading } = useInstantActionDefinitions();
  const [firingSlug, setFiringSlug] = useState<string | null>(null);

  // Per-contact availability gating (requires-contact-fields plan,
  // 2026-08-13) — computed off EXISTING props only, no new fetch/effect/ref.
  const contactFieldValues = useMemo<Record<string, string | null>>(
    () => ({ id: contactId, email: contactEmail, account_id: accountId }),
    [contactId, contactEmail, accountId],
  );
  const { available, unavailable } = useMemo(() => {
    const avail: InstantActionDefinition[] = [];
    const unavail: Array<{ def: InstantActionDefinition; missing: InstantActionDefinition['required_contact_fields'] }> = [];
    // `Array.isArray` guard (not just `?? []`): `validateResponse` is
    // observational (see its doc comment) — a response that fails the
    // top-level `z.array(...)` check (e.g. a malformed/non-array payload)
    // comes back as whatever the server actually sent, which may not be
    // iterable at all. Without this guard a non-array `definitions` would
    // throw out of this `for...of` and crash the whole dropdown tree instead
    // of degrading to "show nothing gated".
    for (const def of Array.isArray(definitions) ? definitions : []) {
      const missing = missingRequiredFields(def, contactFieldValues);
      if (missing.length > 0) {
        unavail.push({ def, missing });
      } else {
        avail.push(def);
      }
    }
    return { available: avail, unavailable: unavail };
  }, [definitions, contactFieldValues]);

  const fire = async (def: InstantActionDefinition) => {
    if (readOnly || firingSlug) return;
    setFiringSlug(def.slug);
    try {
      // Two failure shapes on the wire: a route-level 4xx/5xx `problem()`
      // body (action-routes.ts: unknown definition, bad body, unexpected
      // failure), whose sentence `ApiProblemError.userMessage` already reads
      // (`detail`, else `title`), and a business-logic 422 (`ExecutionResult`'s
      // own `{ status: 'error', message }` — e.g. disabled definition, input
      // validation) straight from `executeUnbound`, read off `err.body` by the
      // same schema the success path uses.
      let data: ExecuteActionResponse;
      try {
        data = await fetchJson(apiFetch, ExecuteActionResponseSchema, `/api/v4/actions/${def.slug}/execute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inputs: {},
            contact_id: contactId,
            contact_email: contactEmail,
            account_id: accountId,
          }),
        });
      } catch (err) {
        // apiFetch already announced a refusal.
        if (err instanceof WriteRefusedError) return;
        if (!(err instanceof ApiProblemError) || err.kind !== 'http') throw err;
        const failure = ExecuteActionResponseSchema.safeParse(err.body);
        const message = (failure.success ? failure.data.error?.message || failure.data.message : undefined) || err.userMessage;
        toast.error(message || `Failed to fire ${def.name}`);
        return;
      }
      if (data.status !== 'ok' || !data.executionId) {
        toast.error(data.error?.message || data.message || `Failed to fire ${def.name}`);
        return;
      }
      // R5: confirmed-success feedback FIRST — a rep who fired the action
      // must see it land (sidebar detail + toast) regardless of what happens
      // next. The optimistic cache prepend is best-effort and strictly last.
      openRequestDetail(data.executionId);
      toast.success(`${def.name} fired for ${contactName ?? 'contact'}`);
      // Host dismissal (e.g. the search card popover closing so the Actions
      // detail it just opened is actually visible) — after the confirmed
      // feedback, before the best-effort prepend.
      onFired?.();
      try {
        const entry: RequestEntry = {
          id: data.executionId,
          kind: 'action',
          contact: { id: contactId, name: contactName, email: contactEmail },
          actionName: def.name,
          status: 'running',
          ts: new Date().toISOString(),
          inputs: {},
          response: null,
          appliedSnapshot: null,
        };
        prependOptimisticRequest(queryClient, entry);
      } catch {
        // Best-effort only — the SSE-triggered refetch (or the next manual
        // read) still lands the real row. Never let a cache-write failure
        // swallow the success feedback already shown above.
      }
    } catch (err) {
      // An AbortError means our own client timeout fired — the request may
      // still be running server-side, so don't claim it failed outright.
      const aborted =
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof ApiProblemError && err.kind === 'aborted');
      toast.error(
        aborted
          ? `${def.name} is taking a while — it may still be running; check Actions`
          : `Failed to fire ${def.name}`,
      );
    } finally {
      setFiringSlug(null);
    }
  };

  return { definitions, isLoading, available, unavailable, firingSlug, fire };
}

export interface UseEnrollContactArgs {
  contactId: string;
  contactName: string | null;
  contactEmail: string | null;
  /**
   * Called after a successful enroll (the sidebar has already opened on the
   * Plays tab showing the new item). Same purpose as `onFired`: lets an
   * overlaying host get out of the way of the surface the action just opened.
   */
  onEnrolled?: () => void;
  /**
   * Gates the (per-contact, per-open) `useContactStateDetail` fetch that
   * `warmIntroAvailable` depends on — replaces the pre-lift `open` (the
   * caller's own menu-open boolean); the caller decides when its catalog UI
   * is visible and passes that through here.
   */
  catalogOpen: boolean;
}

/**
 * "Queue for Play ▾" logic — enroll into a live play. Reuses the exact rail
 * every other enroll affordance uses (`useEnrollQueue().plays`/`PlayPicker`
 * for the catalog UI; `addContact`/`assignPlay` to write) — no new enroll
 * path. If the contact already has a queue item (any status), the play is
 * assigned onto that existing item (`assignPlay`); otherwise a new item is
 * added WITH the play in one request (`addContact`'s optional `playTypeId`/
 * `playInstanceRef`, the same one-shot rail `StatePills.tsx`'s "Suggested
 * Invite" button uses).
 *
 * `enroll()`/`addUnassigned()` do NOT close any caller-owned menu (the one
 * intentional behavioural subtraction from the pre-lift handlers) — the
 * caller owns its own menu's open state.
 */
export function useEnrollContact({ contactId, contactName, contactEmail, onEnrolled, catalogOpen }: UseEnrollContactArgs) {
  const {
    plays, activePlayTypes, gongFlowsSuggested, groups, addContact, assignPlay,
    readOnly, readOnlyReason, playsResolved, openQueueTab, requestQueueFlash, requestPlayCardLanding,
  } = useEnrollQueue();
  const [pending, setPending] = useState(false);

  const existingItem = groups.flatMap(g => g.items).find(it => it.contact_id === contactId) ?? null;
  const queued = existingItem !== null;
  const catalogLoading = !playsResolved;

  // Mirror the gate that will actually run: an existing item is assigned via
  // PATCH (validated against the item's SNAPSHOTTED contact_email), a fresh
  // add is validated against the LIVE contact email.
  const contactHasEmailForPicker = (existingItem ? existingItem.contact_email : contactEmail) !== null;

  // The play catalog (`plays`, above) is contact-agnostic, so Warm Intro
  // availability comes from this per-contact state read. Fetched only while
  // `catalogOpen` — per-row menus × up to 500 rows/page makes an
  // unconditional fetch too expensive. Warm Intro stays hidden while
  // loading, on error, and until a `warm_intro` fact resolves.
  const { data: stateDetail, isLoading: stateDetailLoading, error: stateDetailError } = useContactStateDetail(catalogOpen ? contactId : null);
  const warmIntroAvailable = !stateDetailLoading && !stateDetailError && Array.isArray(stateDetail?.facts)
    && stateDetail.facts.some(fact => fact.dimension === 'warm_intro');

  /**
   * QA round 2 (R2) — a successful enroll ALWAYS opens the docked sidebar on
   * the Queue tab (`openQueueTab`). flow-contacts-bulk-send U7 (decision 12):
   * the confirmation toast is replaced by the SAME landing bulk-add uses
   * (`requestPlayCardLanding` forces the target play's card open with a "N
   * added, M skipped" banner — here always `added: 1, skipped: 0`, since a
   * single direct enroll either succeeds or throws).
   */
  const enroll = async (play: PlayOption) => {
    if (readOnly || pending) return;
    setPending(true);
    try {
      if (existingItem) {
        await assignPlay(existingItem.id, play);
      } else {
        await addContact(contactId, {
          sourceContext: 'contact-workflows-menu',
          playTypeId: play.play_type_id,
          playInstanceRef: play.play_instance_ref,
        });
      }
      requestPlayCardLanding({ playTypeId: play.play_type_id, playInstanceRef: play.play_instance_ref, added: 1, skipped: 0, source: 'single' });
      openQueueTab();
      // Host dismissal — the queued item just landed on the Plays surface
      // underneath; an overlaying host (search card popover) closes so the
      // rep can see it.
      onEnrolled?.();
    } catch (err) {
      // A write refusal (read-only impersonation) is already surfaced by the
      // fetch layer. Otherwise show the server's own problem() message (via
      // addContact's mapped Error), never a bare status code.
      if (err instanceof WriteRefusedError) return;
      toast.error(err instanceof Error && err.message ? err.message : `Couldn't add ${contactName ?? 'contact'} to ${play.name}`);
    } finally {
      setPending(false);
    }
  };

  /**
   * "Add to Queue (unassigned)" — enqueue the contact with NO play chosen
   * yet (`addContact` with no `playTypeId`/`playInstanceRef`) and route the
   * rep to the sidebar's Queue tab, where the unassigned row's own inline
   * `PlayPicker` offers the full catalog. `requestQueueFlash(contactId, {
   * autoOpenPicker: true })` asks the corresponding `UnassignedRow` to
   * auto-reveal its own picker AND that picker's browse panel, so the
   * sidebar opens already showing exactly where the rep was headed.
   */
  const addUnassigned = async () => {
    if (readOnly || pending) return;
    setPending(true);
    try {
      await addContact(contactId, { sourceContext: 'contact-workflows-menu' });
      requestQueueFlash(contactId, { autoOpenPicker: true });
      openQueueTab();
      toast.success('Added to queue');
      onEnrolled?.();
    } catch {
      toast.error('Failed to add to queue');
    } finally {
      setPending(false);
    }
  };

  return {
    queued,
    existingItem,
    pending,
    catalogLoading,
    contactHasEmailForPicker,
    warmIntroAvailable,
    readOnly,
    readOnlyReason,
    plays,
    activePlayTypes,
    gongFlowsSuggested,
    enroll,
    addUnassigned,
    openQueueTab,
  };
}
